import {ArrayUtils, RandomUtils, TimeUtils} from '@colonist/utils'
import {CardEnum, PlayerColor, TradeId, TradeOfferState, TradeResponseStatus} from '@colonist/game-states'
import {TradeBankRatio} from './TradeBankRatioState'
import {TradeCreationData, TradeError, TradeErrorHighlightLocationEnum} from './TradeTypes'
import {GameSpeedSettings} from '@colonist/types'

export module TradeUtils {
    export const tradeMaximumWantedResources = 6
    export const delayClosingTradeWhenAllPlayersReject = 1500
    export const DEFAULT_BANK_TRADE_RATIO = 4
    export interface TradeAutomaticResponseDelayBounds {
        mininumTime: number
        maximumTime: number
    }

    export function getRandomTradeDelayBetweenBounds(automaticTradeResponseDelayBounds: TradeAutomaticResponseDelayBounds): number {
        return RandomUtils.randomIntBetween(automaticTradeResponseDelayBounds.mininumTime, automaticTradeResponseDelayBounds.maximumTime)
    }

    export const botGamePlayerAutoRejectTradeIfDoesntHaveResourcesDelay: TradeAutomaticResponseDelayBounds = {
        mininumTime: 4000,
        maximumTime: 6000,
    }

    export const humanGamePlayerAutoRejectTradeIfDoesntHaveResourcesDelay: TradeAutomaticResponseDelayBounds = {
        mininumTime: 5000,
        maximumTime: 7000,
    }

    export const botTradeFastResponseDelay: TradeAutomaticResponseDelayBounds = {
        mininumTime: 500,
        maximumTime: 1000,
    }

    export const botTradeSlowResponseDelay: TradeAutomaticResponseDelayBounds = {
        mininumTime: 4000,
        maximumTime: 6000,
    }

    export const botTradeExecutionIfAllPlayersHaveRespondedDelay: TradeAutomaticResponseDelayBounds = {
        mininumTime: 500,
        maximumTime: 1000,
    }

    export const botTradeExecutionIfWaitingForPlayersResponseDelay: TradeAutomaticResponseDelayBounds = {
        mininumTime: 10000,
        maximumTime: 12000,
    }

    export function isOpenEndedTrade(offeredResources: CardEnum[], wantedResources: CardEnum[]): boolean {
        if(offeredResources.includes(CardEnum.AnyResource)) return true
        if(wantedResources.includes(CardEnum.AnyResource)) return true
        return false
    }

    export function getBankTradeRatioError(offeredResources: CardEnum[], wantedResources: CardEnum[], tradeRatios: {[key in CardEnum]?: number} = {}): TradeError | undefined {
        const uniqueOfferedResources = ArrayUtils.removeDuplicatesFromArray(offeredResources)
        if(uniqueOfferedResources.length <= 0) return {error: {key: 'strings:game.errors.tradeErrors.invalidBankTradeRatios'}, payload: {isBankTradeButtonDisabled: true}}

        if(wantedResources.length == 0) return {error: {key: 'strings:game.errors.tradeErrors.offerIncomplete'}}

        let validNumberOfWantedCards = 0
        const cardsWithInvalidTradeRatios: CardEnum[] = []
        for(const offeredResource of uniqueOfferedResources) {
            const tradeRatio = tradeRatios[offeredResource] ?? 4
            const offeredResourceAmount = ArrayUtils.countOccurenceOfValueInArray(offeredResources, offeredResource)
            if(offeredResourceAmount % tradeRatio != 0) {
                cardsWithInvalidTradeRatios.push(offeredResource)
                continue
            }

            const offeredAmount = offeredResourceAmount / tradeRatio
            if(offeredAmount < 1) {
                cardsWithInvalidTradeRatios.push(offeredResource)
                continue
            }

            validNumberOfWantedCards += offeredAmount
        }

        if(cardsWithInvalidTradeRatios.length > 0) {
            return {
                error: {key: 'strings:game.errors.tradeErrors.invalidBankTradeRatios'},
                payload: {
                    isBankTradeButtonDisabled: true,
                    highlightCards: {
                        cardsToHighlight: cardsWithInvalidTradeRatios,
                        locations: [TradeErrorHighlightLocationEnum.ProposalOffered],
                    },
                },
            }
        }

        const allWantedCards = ArrayUtils.removeDuplicatesFromArray(wantedResources)
        if(validNumberOfWantedCards != wantedResources.length) {
            return {
                error: {
                    key: 'strings:game.errors.tradeErrors.bankTradeIncorrectWantedCardCount',
                    options: {
                        count: validNumberOfWantedCards,
                    }},
                payload: {
                    isBankTradeButtonDisabled: true,
                    highlightCards: {
                        cardsToHighlight: allWantedCards,
                        locations: [TradeErrorHighlightLocationEnum.ProposalWanted],
                    },
                },
            }
        }

        return undefined
    }

    export function getBankTradeRatioForCard(bankTradeRatios: TradeBankRatio[], card: CardEnum) {
        const ratio = bankTradeRatios.find((bankTradeRatio) => bankTradeRatio.resource == card)?.tradeRatio
        return ratio ?? DEFAULT_BANK_TRADE_RATIO
    }

    export function createPlayerTradeState(playerColors: PlayerColor[], tradeId: TradeId, tradeData: TradeCreationData, currentTurnPlayerColors: PlayerColor[]): TradeOfferState {
        const isCounterOffer = tradeData.counterOfferInResponseToTradeId != undefined
        const initialPlayerResponses: {[key in PlayerColor]?: TradeResponseStatus} = {}
        for(const playerColor of playerColors) {
            const isPlayerTradeExecutor = isCounterOffer ? currentTurnPlayerColors.includes(playerColor) : playerColor == tradeData.creator
            if(isPlayerTradeExecutor) continue
            initialPlayerResponses[playerColor] = TradeResponseStatus.PendingResponse
        }

        const initialPlayersCreatingCounterOffers: {[key in PlayerColor]?: boolean} = {}
        for(const playerColor of playerColors) {
            initialPlayersCreatingCounterOffers[playerColor] = false
        }

        return {
            id: tradeId,
            creator: tradeData.creator,
            offeredResources: tradeData.offeredResources,
            wantedResources: tradeData.wantedResources,
            playerResponses: initialPlayerResponses,
            counterOfferInResponseToTradeId: tradeData.counterOfferInResponseToTradeId,
            playersCreatingCounterOffer: initialPlayersCreatingCounterOffers,
            ...(tradeData.forcedResponse != undefined ? {forcedResponse: tradeData.forcedResponse} : undefined),
        }
    }

    export function getDefaultTradeAutoRejectionTimeByGameSpeed(gameSpeed: GameSpeedSettings): number {
        switch(gameSpeed) {
            case GameSpeedSettings.Base30s: return 8 * TimeUtils.MILLISECONDS
            case GameSpeedSettings.Base60s: return 10 * TimeUtils.MILLISECONDS
            case GameSpeedSettings.Base120s: return 13 * TimeUtils.MILLISECONDS
            case GameSpeedSettings.Base240s: return 15 * TimeUtils.MILLISECONDS
            case GameSpeedSettings.Base12000s: return 20 * TimeUtils.MILLISECONDS
            case GameSpeedSettings.Rush5s: return 4 * TimeUtils.MILLISECONDS
            case GameSpeedSettings.Rush8s: return 6 * TimeUtils.MILLISECONDS
            case GameSpeedSettings.Rush10s: return 8 * TimeUtils.MILLISECONDS
            case GameSpeedSettings.Rush15s: return 12 * TimeUtils.MILLISECONDS
            case GameSpeedSettings.Rush20s: return 15 * TimeUtils.MILLISECONDS
        }
    }
}
